import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskReveal } from "@/components/site/Reveal";
import { useAmazonLink } from "@/hooks/useAmazonLink";
import { ASSETS } from "@/lib/site";

export default function Hero() {
  const ref = useRef(null);
  const { link: amazonLink } = useAmazonLink();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Parallax for the book cover + subtle counter-drift for the title.
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const coverRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="vignette relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
    >
      {/* Oversized ghost number */}
      <span className="pointer-events-none absolute -left-6 top-24 select-none font-display text-[26vw] leading-none text-white/[0.03] md:text-[20vw]">
        01
      </span>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-6 md:px-12 lg:grid-cols-12">
        {/* Left: kinetic headline */}
        <motion.div style={{ y: titleY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-rebellion"
          >
            A political reckoning — not science fiction
          </motion.p>

          <h1 className="font-display text-6xl uppercase leading-[0.88] tracking-tight text-paper sm:text-7xl md:text-8xl">
            <MaskReveal
              lines={["Democracy", "is a Monarchy", <span key="d" className="text-rebellion">in Disguise.</span>]}
              delay={0.15}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-8 max-w-lg font-editorial text-xl leading-relaxed text-white/70 md:text-2xl"
          >
            What if the exhaustion is the point?{" "}
            <em className="text-paper">Election on Mars</em> traces how modern
            democracies wear you down until you stop asking — and imagines what
            it might look like to start again.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              data-testid="hero-buy-button"
              href={amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-3 overflow-hidden border border-rebellion bg-rebellion px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-transparent hover:text-rebellion"
            >
              Read the Book
              <ArrowDown className="h-4 w-4 -rotate-90 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">
              Available now on Amazon
            </span>
          </motion.div>
        </motion.div>

        {/* Right: book cover with parallax */}
        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: coverY, rotate: coverRotate }}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-[74%] sm:w-[60%] lg:w-full"
          >
            <div className="absolute -inset-4 -z-10 bg-rebellion/20 blur-2xl" />
            <div className="relative border border-white/15 shadow-[24px_24px_70px_rgba(0,0,0,0.85)]">
              <img
                data-testid="hero-cover-image"
                src={ASSETS.cover}
                alt="Election on Mars book cover"
                className="block w-full"
              />
              <div className="pointer-events-none absolute inset-0 shadow-[inset_-14px_0_40px_rgba(0,0,0,0.45)]" />
            </div>
            <span className="mt-4 block text-right font-mono text-[0.65rem] uppercase tracking-[0.3em] text-white/40">
              Ayush Garodia
            </span>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/40">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce text-rebellion" />
      </motion.div>
    </section>
  );
}
